import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, Mail } from "lucide-react";

const LOGO_URL = "/back-at-one.png";

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [clicks, setClicks] = useState(0);
  const [unlocked, setUnlocked] = useState(false);

  const onTitleClick = () => {
    const next = clicks + 1;
    setClicks(next);
    if (next >= 5) setUnlocked(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  // ---------- Fake 404 landing ----------
  if (!unlocked) {
    return (
      <div
        data-testid="error-404-screen"
        className="min-h-screen bg-white flex flex-col items-center justify-center px-6 select-none"
      >
        <button
          type="button"
          data-testid="error-404-trigger"
          onClick={onTitleClick}
          className="font-mono text-7xl sm:text-8xl font-bold text-slate-900 tracking-tight cursor-default focus:outline-none"
          aria-label="error"
        >
          404
        </button>
        <p className="mt-4 font-mono text-sm text-slate-500">
          This page could not be found.
        </p>
      </div>
    );
  }

  // ---------- Real login form ----------
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img
            src={LOGO_URL}
            alt="Back at One"
            loading="eager"
            className="h-28 w-auto object-contain"
            data-testid="login-logo"
          />
        </div>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                data-testid="login-email-input"
                type="email"
                required
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                placeholder="you@company.com"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="off"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
